import { PlatformClient } from '../PlatformClient';
import { v4 as uuid } from 'uuid';
import { PlatformUtil, DevicePerformanceClassifier, DeviceCapability } from '@quickplay/foundation';

import { Resolution, KeySystems } from '@quickplay/player-interface';

export interface Codecs {
  [key: string]: {
    name: string;
    flags?: { [key: string]: any };
  };
}

/**
 * Reference to Tizen API
 */
declare const tizen: any;
/**
 * Reference to webapis (Tizen)
 */
declare const webapis: any;
/**
 * Reference to WebOS API
 */
declare const webOS: any;
/**
 * Reference to Hisense/VIDAA HDR capability API
 */
declare const Hisense_GetSupportForHDRInfo: (() => any) | undefined;
/**
 * Device manufacturer for WebOS device
 */
const WEBOS_DEVICE_MANUFACTURER = 'LG Electronics Inc';
const LG_WEB_OS = 'Web0S';
const SAMSUNG_TIZEN_OS = 'Tizen';
const VIERA_OS = 'Viera';
const VIERA_DEVICE_MANUFACTURER = 'Panasonic';

/**
 * HDR format
 */
const HDR_DOLBY_VISION = 'dolby-vision';
const HDR_HDR10 = 'hdr10';
const HDR_HDR10_PLUS = 'hdr10-plus';
const HDR_HLG = 'hlg';

const widevineSecurityLevelsMap: Map<string | undefined, string | undefined> = new Map([
  ['SW_SECURE_CRYPTO', 'L3'],
  ['SW_SECURE_DECODE', 'L3'],
  ['HW_SECURE_CRYPTO', 'L2'],
  ['HW_SECURE_DECODE', 'L1'],
  ['HW_SECURE_ALL', 'L1'],
]);

export const createPlatformClient = (clientId?: string, clientName?: string): PlatformClient => {
  let deviceManufacturer_: string | undefined = undefined;
  let deviceModelName_: string | undefined = undefined;
  let deviceModelNumber_: string | undefined = undefined;
  let deviceYear_: string | undefined = undefined;
  let deviceOs_: string | undefined = undefined;
  let deviceOsVersion_: string | undefined = undefined;
  let supportedResolution_: Resolution | undefined = undefined;
  const supportedAudio_: string | undefined = undefined;
  let supportedAudioCodecs_: string | undefined = undefined;
  let supportedVideoCodecs_: string | undefined = undefined;
  let deviceCapability_: DeviceCapability | undefined = undefined;
  let supportedMaxWVSecurityLevel_: string | undefined = undefined;
  let doesTVSupportDolbyAtmos: boolean | undefined = undefined;
  let supportedHdrCapabilities_: string | undefined = undefined;

  getDeviceInfo();
  getSupportedAudioCodecs();
  getSupportedVideoCodecs();
  getSupportedMaxWVSecurityLevel();
  getSupportedHdrCapabilities();
  getDeviceCapability();

  const platformName_ = (): string => {
    if (PlatformUtil.isTizen()) {
      return 'samsungtizentv';
    } else if (PlatformUtil.isWebOS()) {
      return 'lgwebostv';
    } else if (PlatformUtil.isPS4()) {
      return 'ps4';
    } else if (PlatformUtil.isPS5()) {
      return 'ps5';
    } else if (PlatformUtil.isXbox()) {
      return 'xbox';
    } else if (PlatformUtil.isWPE()) {
      return 'x1';
    } else if (PlatformUtil.isChromecast()) {
      return 'castclient';
    } else if (PlatformUtil.isVizio()) {
      return 'viziotv';
    } else if (PlatformUtil.isVIDAA()) {
      return 'vidaatv';
    } else if (PlatformUtil.isPanasonicTV()) {
      return 'panasonictv';
    } else {
      return 'web';
    }
  };

  function getDeviceInfo(): void {
    if (PlatformUtil.isTizen()) {
      getTizenInfo();
    } else if (PlatformUtil.isWebOS()) {
      getWebOSInfo();
    } else if (PlatformUtil.isPanasonicTV()) {
      getPanasonicTVInfo();
    }
  }

  /**
   * Device information from Tizen:
   * - Manufacturer (Example: Samsung)
   * - Model name (Example: QTQ60)
   * - Model number (Example: QN65Q60TAPXPA)
   * - Supported resolution (Possible values: FHD, 4K, 8K)
   */
  function getTizenInfo(): void {
    // Extract Tizen version
    const tizenRegex = /Tizen\s*([\d.]+)/i;
    const tizenMatch = tizenRegex.exec(navigator.userAgent);
    deviceOs_ = SAMSUNG_TIZEN_OS;
    deviceOsVersion_ = tizenMatch ? tizenMatch[1] : undefined;

    // Map Tizen version to TV model year
    const versionMap: Record<string, string> = {
      '10.0': '2026',
      '9.0': '2025',
      '8.0': '2024',
      '7.0': '2023',
      '6.5': '2022',
      '6.0': '2021',
      '5.5': '2020',
      '5.0': '2019',
      '4.0': '2018',
      '3.0': '2017',
      '2.4': '2016',
      '2.3': '2015',
    };
    deviceYear_ = deviceOsVersion_ ? versionMap[deviceOsVersion_] : undefined;

    if (typeof tizen !== 'undefined' && tizen.systeminfo && tizen.systeminfo !== 'undefined') {
      deviceManufacturer_ = tizen.systeminfo.getCapability?.('http://tizen.org/system/manufacturer');
    }

    if (typeof webapis !== 'undefined' && webapis.productinfo && webapis.productinfo !== 'undefined') {
      deviceModelName_ = webapis.productinfo.getModel?.();
      deviceModelNumber_ = webapis.productinfo.getRealModel?.();

      if (webapis.productinfo.is8KPanelSupported?.()) {
        supportedResolution_ = Resolution['8K'];
      } else if (webapis.productinfo.isUdPanelSupported?.()) {
        supportedResolution_ = Resolution['4K'];
      }
    }
  }

  /**
   * Device information from LG Web0S:
   * - Model name (Example: 43UH6100-SJ)
   * - Supported resolution (Possible values: FHD, 4K, 8K)
   */
  function getWebOSInfo(): void {
    deviceOs_ = LG_WEB_OS;
    deviceManufacturer_ = WEBOS_DEVICE_MANUFACTURER;
    // Map Chromium version to LG TV model year
    const chromiumVersionToTvYearMap: Record<string, string> = {
      '132': '2026',
      '120': '2025',
      '108': '2024',
      '94': '2023',
      '87': '2022',
      '79': '2021',
      '68': '2020',
      '53': '2018~2019',
      '38': '2016~2017',
      '538': '2015',
      '537': '2014',
    };

    /// Extract Chromium version from webOS User-Agent and get TV year
    const match = /web0s.*?chrome\/(\d+)/i.exec(navigator.userAgent);
    deviceYear_ = match ? chromiumVersionToTvYearMap[match[1]] || 'Unknown' : 'Unknown';

    if (typeof webOS !== 'undefined') {
      webOS.deviceInfo((device: any) => {
        deviceModelName_ = device.modelName;
        deviceOsVersion_ = device.version;

        // To check the Dolby atmos supported
        doesTVSupportDolbyAtmos = device.dolbyAtmos;

        // LG-specific HDR signaling (dolby-vision, hdr10); hdr10-plus/hlg are not exposed by this API
        const hdrCapabilities: string[] = [];
        if (device.dolbyVision) {
          hdrCapabilities.push(HDR_DOLBY_VISION);
        }
        if (device.hdr10) {
          hdrCapabilities.push(HDR_HDR10);
        }
        supportedHdrCapabilities_ = hdrCapabilities.length > 0 ? hdrCapabilities.join(',') : undefined;

        if (!!device.uhd8K) {
          supportedResolution_ = Resolution['8K'];
        } else if (!!device.uhd) {
          supportedResolution_ = Resolution['4K'];
        } else if (device.screenWidth > 1920 && device.screenHeight > 1080) {
          supportedResolution_ = Resolution['2K'];
        } else if (device.screenWidth === 1920 && device.screenHeight === 1080) {
          supportedResolution_ = Resolution.FHD;
        } else if (device.screenWidth === 1280 && device.screenHeight === 720) {
          supportedResolution_ = Resolution.HD;
        }
      });
    }
  }

  /**
   * Device information from Panasonic VIERA TVs:
   * - Device OS (VIERA proprietary OS)
   * - Manufacturer (Panasonic)
   * - Device year (Example: 2016, 2020) — extracted from HbbTV user agent if available
   * - OS / firmware version (Example: 3.280, 4.417)
   * - Model name (Not available in user agent, defaults to "Unknown")
   *
   * Notes:
   * - Supports both HbbTV-based and WebKit-based user agent formats
   * - Falls back to "Unknown" when required information is not present
   */
  function getPanasonicTVInfo(): void {
    deviceOs_ = VIERA_OS;
    deviceManufacturer_ = VIERA_DEVICE_MANUFACTURER;

    const ua = navigator.userAgent;

    // HbbTV UA: HbbTV/1.2.1 (;Panasonic;VIERA 2016;3.280;7401-0003 0002-0000;)
    const hbbtvMatch = /HbbTV\/[\d.]+\s*\([^)]*VIERA\s+(\d{4});([\d.]+)/.exec(ua);

    // WebKit UA: Mozilla/5.0 (Linux; U; Viera; en-GB) ... Viera/3.20.0
    const webkitMatch = /Viera\/([\d.]+)/.exec(ua);

    if (hbbtvMatch) {
      deviceYear_ = hbbtvMatch[1]; // e.g. "2016"
      deviceOsVersion_ = hbbtvMatch[2]; // e.g. "3.280"
    } else if (webkitMatch) {
      deviceYear_ = 'Unknown'; // not present in WebKit UA
      deviceOsVersion_ = webkitMatch[1]; // e.g. "3.20.0"
    } else {
      deviceYear_ = 'Unknown';
      deviceOsVersion_ = 'Unknown';
    }

    deviceModelName_ = 'Unknown'; // not exposed in UA
  }

  /**
   * Returns the supported audio codecs on the streaming device.
   */
  function getSupportedAudioCodecs(): void {
    const codecs: Codecs = {
      ac3: { name: 'ac-3' },
      eac3: { name: 'ec-3' },
      mhm1: { name: 'mhm1.0x0D' },
      mp4a: { name: 'mp4a.40.2' },
      'eac3-joc': { name: 'ec-3', flags: { spatialRendering: true } },
    };

    supportedAudioCodecs_ = getSupportedCodecs('audio', codecs);
  }

  /**
   * Removes 'eac3-joc' from supportedAudioCodecs if Dolby Atmos isn't supported.
   * @param supportedAudioCodecs - Comma-separated audio codecs (e.g., "ac3,eac3-joc")
   * @param isDolbyAtmosSupported - True: keeps all codecs, False: removes 'eac3-joc'
   * @returns Filtered codecs string or undefined
   */
  function processSupportedAudioCodecs(
    supportedAudioCodecs: string | undefined,
    isDolbyAtmosSupported: boolean | undefined,
  ): string | undefined {
    if (isDolbyAtmosSupported === false) {
      // Filter out 'eac3-joc' codec if Dolby Atmos is not supported
      return supportedAudioCodecs
        ?.split(',')
        .filter(codec => codec.trim() !== 'eac3-joc')
        .join(',');
    }

    // Return original codecs if Dolby Atmos support is undefined or true
    return supportedAudioCodecs;
  }

  /**
   * Returns the supported video codecs on the streaming device.
   */
  function getSupportedVideoCodecs(): void {
    const codecs: Codecs = {
      avc: { name: 'avc1.64001E' },
      hevc: { name: 'hev1.1.6.L93.90' },
      vp8: { name: 'vp8' },
      vp9: { name: 'vp9' },
      av01: { name: 'av01.0.01M.08' },
      dvhe: { name: 'dvhe.05.07' },
      dvh1: { name: 'dvh1.05.06' },
    };
    supportedVideoCodecs_ = getSupportedCodecs('video', codecs);
  }

  /**
   * Returns the supported HDR formats on the streaming device.
   * LG (webOS) is detected via the platform deviceInfo API in getWebOSInfo().
   * VIDAA (Hisense) is detected via the platform's Hisense_GetSupportForHDRInfo() API.
   * Tizen is detected via Samsung's webapis.avinfo.isHdrTvSupport() gate plus a HEVC Main10 codec support check.
   */
  function getSupportedHdrCapabilities(): void {
    if (PlatformUtil.isVIDAA()) {
      getVIDAAHdrCapabilities();
      return;
    }

    if (!PlatformUtil.isTizen()) {
      return;
    }

    let isHdrTvSupported = false;
    try {
      isHdrTvSupported = !!(
        typeof webapis !== 'undefined' && // NOSONAR: typeof guard required, webapis may not exist on older Tizen runtimes
        webapis.avinfo &&
        typeof webapis.avinfo.isHdrTvSupport === 'function' &&
        webapis.avinfo.isHdrTvSupport()
      );
    } catch {}

    if (!isHdrTvSupported || typeof MediaSource === 'undefined') {
      supportedHdrCapabilities_ = undefined;
      return;
    }

    const isMain10Supported =
      MediaSource.isTypeSupported('video/mp4; codecs="hev1.2.4.L153.90"') ||
      MediaSource.isTypeSupported('video/mp4; codecs="hvc1.2.4.L153.90"');

    supportedHdrCapabilities_ = isMain10Supported ? HDR_HDR10 : undefined;
  }

  /**
   * Reads HDR capability flags from the VIDAA (Hisense) platform API.
   */
  function getVIDAAHdrCapabilities(): void {
    if (typeof Hisense_GetSupportForHDRInfo === 'undefined') { // NOSONAR: typeof guard required, may not exist on non-VIDAA runtimes
      return;
    }

    try {
      const hdrInfo = Hisense_GetSupportForHDRInfo();

      const hdrCapabilities: string[] = [];
      if (hdrInfo?.DOVI === true) {
        hdrCapabilities.push(HDR_DOLBY_VISION);
      }
      if (hdrInfo?.HDR10 === true) {
        hdrCapabilities.push(HDR_HDR10);
      }
      if (hdrInfo?.HDR10Plus === true) {
        hdrCapabilities.push(HDR_HDR10_PLUS);
      }
      if (hdrInfo?.HLG === true) {
        hdrCapabilities.push(HDR_HLG);
      }

      supportedHdrCapabilities_ = hdrCapabilities.length > 0 ? hdrCapabilities.join(',') : undefined;
    } catch {}
  }

  async function getSupportedMaxWVSecurityLevel(): Promise<void> {
    if (!PlatformUtil.isEdge() || !PlatformUtil.isPS4() || !PlatformUtil.isPS5() || !PlatformUtil.isXbox()) {
      try {
        const videoRobustness = await getWidevineRobustness();
        supportedMaxWVSecurityLevel_ = widevineSecurityLevelsMap.get(videoRobustness);
      } catch {}
    }
  }

  async function getDeviceCapability(): Promise<void> {
    try {
      const result = await DevicePerformanceClassifier.getDeviceCapabilities();
      deviceCapability_ = result;
    } catch {}
  }

  async function getWidevineRobustness(): Promise<string | undefined> {
    const robustnessLevels = [
      'SW_SECURE_CRYPTO', // Widevine L3
      'SW_SECURE_DECODE', // Widevine L3
      'HW_SECURE_CRYPTO', // Widevine L2
      'HW_SECURE_DECODE', // Widevine L1
      'HW_SECURE_ALL', // Widevine L1
    ];

    const videoTestConfig = [
      {
        videoCapabilities: [
          {
            contentType: 'video/mp4; codecs="avc1.64001E"',
            robustness: 'SW_SECURE_DECODE',
          },
        ],
      },
    ];

    let videoRobustness;

    for (let i = 0; i < robustnessLevels.length; i++) {
      const level = robustnessLevels[i];
      videoTestConfig[0].videoCapabilities[0].robustness = level;
      try {
        // eslint-disable-next-line no-await-in-loop
        await navigator.requestMediaKeySystemAccess(KeySystems.WIDEVINE, videoTestConfig);
        videoRobustness = level;
      } catch {}
    }

    return videoRobustness;
  }

  /**
   * Properly formats codec flags for MIME types according to official specifications.
   * Concatenates each flag with its appropriate separator to create a standards-compliant
   * codec string for media playback.
   *
   * @param options - Object containing codec flag key-value pairs to be formatted
   * @returns A properly formatted codec flags string with standard-compliant separators
   *
   * @see https://ott.dolby.com/codec_test/index.html - Implementation follows official
   * Dolby OTT codec detection conventions and specifications for reliable media type recognition
   */
  function formatFlags(options: { [key: string]: any } | undefined): string {
    if (!options || Object.keys(options).length === 0) {
      return '';
    }

    // Join flags with semicolon and space, adding a semicolon at the beginning
    return (
      '; ' +
      Object.entries(options)
        .map(([key, value]) => `${key}=${value}`)
        .join('; ')
    );
  }

  /**
   * Gets supported codecs for a media type
   * @param type Media type (audio/video)
   * @param codecs Codec definitions to check
   * @returns Comma-separated list of supported codec keys or undefined
   */
  function getSupportedCodecs(type: string, codecs: Codecs): string | undefined {
    const supportedCodecs: string[] = [];

    if (typeof MediaSource !== 'undefined') {
      Object.keys(codecs).forEach((key: string) => {
        const codecObj = codecs[key];
        const flags = formatFlags(codecObj.flags);
        const mimeType = `${type}/mp4; codecs="${codecObj.name}"${flags}`;

        if (MediaSource.isTypeSupported(mimeType)) {
          supportedCodecs.push(key);
        }
      });
    }

    return supportedCodecs.length > 0 ? supportedCodecs.join(',') : undefined;
  }

  return {
    /**
     * Unique identifier of the client or device.
     */
    get id(): string {
      return clientId ? clientId : uuid();
    },

    /**
     * Name of the client or device.
     */
    get name(): string {
      return clientName ? clientName : platformName_();
    },

    /**
     * Specifies the manufacturer of the device
     */
    get deviceManufacturer(): string | undefined {
      return deviceManufacturer_;
    },

    /**
     * Specifies the model name of the device
     */
    get deviceModelName(): string | undefined {
      return deviceModelName_;
    },

    /**
     * Specifies the model number of the device
     */
    get deviceModelNumber(): string | undefined {
      return deviceModelNumber_;
    },

    /**
     * Specifies the manufactured year of the device
     */
    get deviceYear(): string | undefined {
      return deviceYear_;
    },

    /**
     * Specifies the Operating Systems of the device
     */
    get deviceOs(): string | undefined {
      return deviceOs_;
    },

    /**
     * Specifies the OS version of the device
     */
    get deviceOsVersion(): string | undefined {
      return deviceOsVersion_;
    },

    /**
     * Specifies max supported resolution on the streaming device
     */
    get supportedResolution(): Resolution | undefined {
      return supportedResolution_;
    },

    /**
     * Specifies supported audio format on the streaming device
     */
    get supportedAudio(): string | undefined {
      return supportedAudio_;
    },

    /**
     * Specifies supported video codecs on the streaming device.
     */
    get supportedVideoCodecs(): string | undefined {
      return supportedVideoCodecs_;
    },

    get deviceCapability(): DeviceCapability | undefined {
      return deviceCapability_;
    },

    /**
     * Specifies supported audio codecs on the streaming device.
     */
    get supportedAudioCodecs(): string | undefined {
      return processSupportedAudioCodecs(supportedAudioCodecs_, doesTVSupportDolbyAtmos);
    },

    get supportedMaxWVSecurityLevel(): string | undefined {
      return supportedMaxWVSecurityLevel_;
    },

    /**
     * Specifies supported HDR formats on the streaming device.
     */
    get supportedHdrCapabilities(): string | undefined {
      return supportedHdrCapabilities_;
    },
  };
};
